import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../types';

// Auth selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;

// Vehicle selectors
export const selectVehicles = (state: RootState) => state.vehicles;
export const selectVehiclesList = (state: RootState) => state.vehicles.vehicles || [];
export const selectCurrentVehicle = (state: RootState) => state.vehicles.currentVehicle;
export const selectVehiclesLoading = (state: RootState) => state.vehicles.loading;
export const selectVehiclesError = (state: RootState) => state.vehicles.error;

// Alert selectors with safety checks
export const selectAlerts = (state: RootState) => state.alerts;
export const selectAlertsList = createSelector(
  [selectAlerts],
  (alertsState) => {
    if (!alertsState || !Array.isArray(alertsState.alerts)) {
      return [];
    }
    return alertsState.alerts;
  }
);

export const selectUnreadAlertsCount = createSelector(
  [selectAlertsList],
  (alerts) => {
    if (!Array.isArray(alerts)) {
      return 0;
    }
    return alerts.filter(alert => alert && !alert.is_read).length;
  }
);

export const selectUnresolvedAlertsCount = createSelector(
  [selectAlertsList],
  (alerts) => {
    if (!Array.isArray(alerts)) {
      return 0;
    }
    return alerts.filter(alert => alert && !alert.is_resolved).length;
  }
);

export const selectAlertsLoading = (state: RootState) => state.alerts?.loading || false;
export const selectAlertsError = (state: RootState) => state.alerts?.error || null;

// Parking selectors
export const selectParking = (state: RootState) => state.parking;
export const selectParkingSpots = (state: RootState) => state.parking.spots || [];
export const selectParkingLoading = (state: RootState) => state.parking.loading;
export const selectParkingError = (state: RootState) => state.parking.error;

// Available parking spots
export const selectAvailableParkingSpots = createSelector(
  [selectParkingSpots],
  (spots) => {
    if (!Array.isArray(spots)) {
      return [];
    }
    return spots.filter(spot => spot && !spot.is_occupied);
  }
);

// Occupied parking spots
export const selectOccupiedParkingSpots = createSelector(
  [selectParkingSpots],
  (spots) => {
    if (!Array.isArray(spots)) {
      return [];
    }
    return spots.filter(spot => spot && spot.is_occupied);
  }
);

// Vehicle status statistics
export const selectVehicleStats = createSelector(
  [selectVehiclesList],
  (vehicles) => {
    if (!Array.isArray(vehicles)) {
      return {
        total: 0,
        ready: 0,
        maintenance: 0,
        washing: 0,
        out: 0,
      };
    }

    return vehicles.reduce(
      (stats, vehicle) => {
        if (!vehicle) return stats;
        
        stats.total += 1;
        
        switch (vehicle.status) {
          case 'ready':
            stats.ready += 1;
            break;
          case 'under_maintenance':
            stats.maintenance += 1;
            break;
          case 'washing':
            stats.washing += 1;
            break;
          case 'out':
          case 'returned':
            stats.out += 1;
            break;
          default:
            break;
        }
        
        return stats;
      },
      {
        total: 0,
        ready: 0,
        maintenance: 0,
        washing: 0,
        out: 0,
      }
    );
  }
);

// Priority alerts (high and critical)
export const selectHighPriorityAlerts = createSelector(
  [selectAlertsList],
  (alerts) => {
    if (!Array.isArray(alerts)) {
      return [];
    }
    return alerts.filter(alert => 
      alert && 
      !alert.is_resolved && 
      (alert.priority === 'high' || alert.priority === 'critical')
    );
  }
); 